import { AddCircleOutlineRounded as AddCircleOutlineRoundedIcon } from '@mui/icons-material';
import { Stack, Button, Alert, IconButton } from '@mui/material';
import { AxiosError } from 'axios';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import {
  filter,
  includes,
  isNil,
  keyBy,
  keys,
  map,
  omitBy,
  pick,
  some,
} from 'lodash';
import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import { useLocation } from 'react-router-dom';

import { ordersApi, requisitesApi } from 'api';
import { requisitesGroupApi } from 'api/requisites-groups.api';
import {
  BankSelect,
  DataWrapper,
  FormikNumericField,
  PaymentTypeSelect,
  FormikSelect,
  FormikRadioGroup,
  RequisitesStatusLabel,
  FormControls,
  FormActions,
  FormikTextField,
  RequisitesBlockedStatusLabel,
  CloseFormikDialogResult,
  FormikYesNoRadioGroup,
  TouchErrorsOnLoad,
} from 'components';
import { NEW_ID } from 'constants/common.constants';
import {
  AppConfigArea,
  AppConfigKey,
  FiatCurrencyCode,
  FormFieldType,
  QueryKey,
  RequisitesStatus,
} from 'enums';
import { FormFields } from 'features/form-builder';
import {
  useCurrencies,
  useMutation,
  usePayinRequisitesUtils,
  useAppHistory,
  useUser,
  useUserContext,
  useUserContextUtils,
  useMeta,
  useAppConfigValue,
} from 'hooks';
import { TranslationNamespace } from 'i18n';
import { FormField, Requisites, RequisitesGroup } from 'types';
import { formUtils, validationUtils } from 'utils';

import { CreateRequisiteGroupDialog } from './CreateRequisiteGroupDialog';
import { buildValidator } from './validator';

type Props = {
  id: string;
};

type Values = Omit<
  Requisites,
  | 'id'
  | 'createdAt'
  | 'limitActiveOrders'
  | 'limitCountPerHour'
  | 'limitCountPerDay'
  | 'limitCountPerMonth'
  | 'limitSumPerHour'
  | 'limitSumPerDay'
  | 'limitSumPerMonth'
  | 'automationApiKey'
  | 'verificationStatus'
  | 'verifiedAt'
  | 'orderAutomationType'
> &
  Partial<
    Pick<
      Requisites,
      | 'limitActiveOrders'
      | 'limitCountPerHour'
      | 'limitCountPerDay'
      | 'limitCountPerMonth'
      | 'limitSumPerHour'
      | 'limitSumPerDay'
      | 'limitSumPerMonth'
    >
  >;

const FIELDS_TO_KEEP_ON_CURRENCY_CHANGE: (keyof Values)[] = [
  'limitActiveOrders',
  'limitCountPerHour',
  'limitCountPerDay',
  'limitCountPerMonth',
  'limitSumPerHour',
  'limitSumPerDay',
  'limitSumPerMonth',
  'completedOrdersInMinutes',
  'status',
  'name',
  'groupId',
  'allowTurboDeposit',
  'qrManagerEnabled',
  'counterpartiesOnly',
];

export const RequisitesDetailsForm: React.FC<Props> = ({ id }: Props) => {
  const { t } = useTranslation(TranslationNamespace.Trader, {
    keyPrefix: 'pages.requisites.details',
  });
  const { t: tFields } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.requisites.requisites_details.fields',
  });

  const location = useLocation();
  const { tab, groupId } = location.state || {};

  const queryClient = useQueryClient();

  const { t: tCommon } = useTranslation(TranslationNamespace.Common);

  const { goBack } = useAppHistory();

  const { role, isTrader, isAdminOrTechOperator } = useUser();

  const { isQRSupported } = useUserContextUtils();

  const { requisitesUrl, isVerified, isNSPK } = usePayinRequisitesUtils();

  const { fiatCurrencies, fiatCurrenciesOptions, getFiatCurrencyByCode } =
    useCurrencies();

  const { banks, paymentTypes, tradeMethods, allTradeMethods } =
    useUserContext();

  const { isTurboDepositEnabled } = useMeta();

  const accountNumberQrSupportedCombinationsQueryResult = useAppConfigValue({
    area: AppConfigArea.PayinRequisites,
    key: AppConfigKey.AccountNumberQrSupportedCombinations,
  });

  const [ordersExistsByRequisites, setOrderExistsByRequisites] =
    useState(false);

  const rubFiatCurrencyId = useMemo(
    () => getFiatCurrencyByCode(FiatCurrencyCode.RUB)?.id,
    [getFiatCurrencyByCode],
  );

  const paymentTypeFields = useMemo(
    () => keyBy(paymentTypes, 'id'),
    [paymentTypes],
  );

  const [currentFields, setCurrentFields] = useState<FormField[] | null>(null);

  const requisitesSchema = useMemo(
    () => buildValidator(currentFields),
    [currentFields],
  );

  const isNew = useMemo(() => id === NEW_ID, [id]);

  const [createRequisiteGroupDialogOpen, setRequisitesGroupDialogOpen] =
    useState(false);

  const initialState: Values = useMemo(
    () => ({
      cardInfo: '',
      phone: '',
      email: '',
      cardholder: '',
      status: RequisitesStatus.Inactive,
      isWhiteTriangle: false,
      limitActiveOrders: 100,
      limitCountPerHour: 1000,
      limitCountPerDay: 10000,
      limitCountPerMonth: 100000,
      limitSumPerHour: 10000000,
      limitSumPerDay: 100000000,
      limitSumPerMonth: 1000000000,
      completedOrdersInMinutes: 0,
      minOrderAmount: 0,
      maxOrderAmount: 0,
      paymentTypeId: '',
      bankId: '',
      fiatCurrencyId: '',
      swiftBic: '',
      bic: '',
      idCard: '',
      passportSeries: '',
      passportNumber: '',
      passportIssueDate: '',
      firstName: '',
      lastName: '',
      middleName: '',
      birthDate: '',
      accountNumber: '',
      name: '',
      expirationDate: '',
      groupId: groupId || '',
      accountLastDigits: '',
      taxId: '',
      paymentLink: '',
      paymentString: '',
      qrManagerApiKey: '',
      qrManagerLogin: '',
      sameBank: false,
      allowTurboDeposit: false,
      counterpartiesOnly: false,
      qrManagerEnabled: true,
    }),
    [groupId],
  );

  const normalizeFieldsConfig = useMemo(
    (): Partial<{
      [key in FormFieldType]: { requiredForCurrencies: string[] };
    }> => ({
      [FormFieldType.TaxId]: {
        requiredForCurrencies: rubFiatCurrencyId ? [rubFiatCurrencyId] : [],
      },
    }),
    [rubFiatCurrencyId],
  );

  const isTradeMethodNotSupported = useCallback(
    (values: Values) =>
      values.fiatCurrencyId &&
      values.paymentTypeId &&
      values.bankId &&
      !some(tradeMethods, {
        fiatCurrencyId: values.fiatCurrencyId,
        paymentTypeId: values.paymentTypeId,
        bankId: values.bankId,
        enabled: true,
      }),
    [tradeMethods],
  );

  const [initialValues, setInitialValues] = useState<Values>(initialState);
  // Changing the fiat currency also resets initialValues, but that is the user's own edit,
  // not a load — only the values that came from the server get their errors shown up front.
  const [loadedValues, setLoadedValues] = useState<Values>();

  const normalizeCurrentFieldsToSet = useCallback(
    (fields: FormField[] | null, values: Values) => {
      if (!fields) {
        return fields;
      }

      const shouldUseQR = isQRSupported(
        values.fiatCurrencyId,
        values.paymentTypeId,
        fiatCurrencies,
        paymentTypes,
        accountNumberQrSupportedCombinationsQueryResult.data as any,
      );

      return map(fields, (field) => {
        const config = normalizeFieldsConfig[field.type];
        let resultField = { ...field };

        if (config && field.required !== false) {
          if (includes(config.requiredForCurrencies, values.fiatCurrencyId)) {
            resultField.required = true;
          }
        }

        if (isNSPK(values)) {
          if (
            [
              FormFieldType.QRManagerAPIKey,
              FormFieldType.QRManagerLogin,
            ].includes(field.type)
          ) {
            if (values.qrManagerEnabled) {
              resultField.hidden = false;
              resultField.required =
                field.type === FormFieldType.QRManagerAPIKey;
              resultField.readonly = false;
            } else {
              resultField.hidden = true;
              resultField.required = false;
            }
          }

          if (field.type === FormFieldType.PaymentLink) {
            if (values.qrManagerEnabled) {
              resultField.hidden = true;
              resultField.required = false;
            } else {
              resultField.hidden = false;
              // Payment link is optional when a payment string is provided (SberQR Vietnam)
              resultField.required = !values.paymentString;
            }
          }
        }

        if (field.type === FormFieldType.AccountNumber && shouldUseQR) {
          resultField = {
            ...resultField,
            type: FormFieldType.AccountNumberQR,
          };
        } else if (
          field.type === FormFieldType.AccountNumberQR &&
          !shouldUseQR
        ) {
          resultField = {
            ...resultField,
            type: FormFieldType.AccountNumber,
          };
        }

        return resultField;
      });
    },
    [
      normalizeFieldsConfig,
      fiatCurrencies,
      paymentTypes,
      accountNumberQrSupportedCombinationsQueryResult.data,
      isQRSupported,
      isNSPK,
    ],
  );

  const queryResult = useQuery(
    [QueryKey.Requisites, id],
    () => requisitesApi.findOne(id as string),
    {
      enabled: !isNew,
      onSuccess: (data) => {
        const pickedData = {
          ...initialState,
          ...omitBy(pick(data, keys(initialState)), isNil),
        } as Values;
        setInitialValues(pickedData);
        setLoadedValues(pickedData);
        setCurrentFields(
          data.paymentTypeId
            ? normalizeCurrentFieldsToSet(
                paymentTypeFields[data.paymentTypeId].fields || null,
                pickedData,
              )
            : null,
        );
      },
    },
  );

  const enableRequisitesGroupRefetch = useMemo(
    () => isNew || !!queryResult.data?.traderId,
    [isNew, queryResult.data?.traderId],
  );

  const { data: requisitesGroupsOptions = [] } = useQuery(
    QueryKey.RequisitesGroups,
    () =>
      isAdminOrTechOperator && queryResult.data?.traderId
        ? requisitesGroupApi.getAllTraderAsRole(role)(
            queryResult.data?.traderId,
          )
        : requisitesGroupApi.getAllAsRole(role)(),
    {
      enabled: enableRequisitesGroupRefetch,
      select: formUtils.getOptions,
    },
  );

  const queryResultOrderExistsByRequisites = useQuery(
    [QueryKey.OrderExistsByRequisites, id],
    () => ordersApi.getOrderExistsByRequisitesAsRole(role)(id as string),
    {
      enabled: !isNew,
      onSuccess: (data) => {
        setOrderExistsByRequisites(data.exists);
      },
    },
  );

  const isBlocked = useMemo(
    () => queryResult.data?.status === RequisitesStatus.Blocked,
    [queryResult],
  );

  const canChangeStatus = useMemo(
    () => !!queryResult.data && !isBlocked && isVerified(queryResult.data),
    [isBlocked, queryResult.data, isVerified],
  );

  const navigateBack = useCallback(
    () => goBack(requisitesUrl, { state: { tab } }),
    [requisitesUrl, goBack, tab],
  );

  const canChangeRequisitesPayment = useMemo(
    () =>
      isNew ||
      (!queryResultOrderExistsByRequisites.isLoading &&
        !ordersExistsByRequisites),
    [
      isNew,
      ordersExistsByRequisites,
      queryResultOrderExistsByRequisites.isLoading,
    ],
  );

  const { mutate: createRequisites } = useMutation<
    Requisites,
    AxiosError,
    Partial<Requisites>
  >(requisitesApi.create);

  const { mutate: updateRequisites } = useMutation<
    Requisites,
    AxiosError,
    { id: string; requisites: Partial<Values> }
  >(requisitesApi.update);

  const handleSubmit = useCallback(
    (requisites: Values, formikHelpers: FormikHelpers<Values>) => {
      const options = {
        onSuccess: navigateBack,
        onError: (error: AxiosError) =>
          formikHelpers.setErrors(validationUtils.getFormErrors(error)),
        onSettled: () => formikHelpers.setSubmitting(false),
      };

      formikHelpers.setSubmitting(true);

      // Select only fields that are in the current schema
      const requisitesData = pick(requisites, keys(requisitesSchema.fields));

      if (isNew) {
        createRequisites(requisitesData, options);
      } else {
        updateRequisites({ id, requisites: requisitesData }, options);
      }
    },
    [
      requisitesSchema.fields,
      isNew,
      navigateBack,
      createRequisites,
      updateRequisites,
      id,
    ],
  );

  const statusOptions = useMemo(
    () =>
      map(
        [RequisitesStatus.Active, RequisitesStatus.Inactive],
        (status: RequisitesStatus.Active | RequisitesStatus.Inactive) => ({
          value: status,
          label: <RequisitesStatusLabel status={status} />,
        }),
      ),
    [],
  );

  const getAvailablePaymentTypes = useCallback(
    (values: Values) => {
      const availableTradeMethods = filter(
        isNew ? tradeMethods : allTradeMethods,
        (tradeMethod) => tradeMethod.fiatCurrencyId === values.fiatCurrencyId,
      );
      const availablePaymentTypesIds = new Set(
        map(availableTradeMethods, (tradeMethod) => tradeMethod.paymentTypeId),
      );
      const availablePaymentTypes = filter(paymentTypes, (paymentType) =>
        availablePaymentTypesIds.has(paymentType.id),
      );
      return availablePaymentTypes;
    },
    [paymentTypes, tradeMethods, allTradeMethods, isNew],
  );

  const getAvailableBanks = useCallback(
    (values: Values) => {
      const availableTradeMethods = filter(
        isNew ? tradeMethods : allTradeMethods,
        (tradeMethod) =>
          tradeMethod.fiatCurrencyId === values.fiatCurrencyId &&
          tradeMethod.paymentTypeId === values.paymentTypeId,
      );
      const availableBanksIds = new Set(
        map(availableTradeMethods, (tradeMethod) => tradeMethod.bankId),
      );
      const availableBanks = filter(banks, (bank) =>
        availableBanksIds.has(bank.id),
      );
      return availableBanks;
    },
    [banks, tradeMethods, allTradeMethods, isNew],
  );

  const handleOnChangePaymentType = useCallback(
    (paymentTypeId: string, formik: FormikProps<Values>) => {
      formik.setFieldValue('bankId', '');
      formik.setTouched({ bankId: false });
      const updatedValues = { ...formik.values, paymentTypeId };
      setCurrentFields(
        normalizeCurrentFieldsToSet(
          paymentTypeFields[paymentTypeId]?.fields || null,
          updatedValues,
        ),
      );
      setTimeout(() => formik.validateForm()); // Dirty hack to validate form after fields change, because Formik doesn't do it automatically
    },
    [paymentTypeFields, normalizeCurrentFieldsToSet],
  );

  // Reset form to initial state when fiat currency changes
  const handleOnChangeFiatCurrency = useCallback(
    (fiatCurrencyId: string, formik: FormikProps<Values>) => {
      const newValues = {
        ...initialState,
        ...pick(formik.values, FIELDS_TO_KEEP_ON_CURRENCY_CHANGE),
        fiatCurrencyId,
      };
      setInitialValues(newValues);
      setCurrentFields(null);
    },
    [initialState],
  );

  const { mutate: createRequisiteGroup } = useMutation<
    RequisitesGroup,
    AxiosError,
    Partial<RequisitesGroup>
  >(requisitesGroupApi.createAsRole(role));

  const handleCreateRequisiteGroupDialogClose = useCallback(
    (formik: FormikProps<Values>) =>
      ({ ok, data }: CloseFormikDialogResult<Partial<RequisitesGroup>>) => {
        if (ok && data) {
          const { values, formikHelpers } = data;
          return createRequisiteGroup(values, {
            onSuccess: (result: RequisitesGroup) => {
              formikHelpers.resetForm();
              setRequisitesGroupDialogOpen(false);
              queryClient.invalidateQueries(QueryKey.RequisitesGroups);
              formik.setFieldValue('groupId', result.id);
            },
            onError: (error: AxiosError) => {
              formikHelpers.setErrors(validationUtils.getFormErrors(error));
            },
          });
        }
        setRequisitesGroupDialogOpen(false);
      },
    [createRequisiteGroup, queryClient],
  );

  return (
    <DataWrapper queryResult={!isNew ? queryResult : undefined}>
      <div className="tw-max-w-md">
        {!canChangeRequisitesPayment && (
          <Alert severity="warning" className="tw-mb-4">
            {t('requisites_in_use')}
          </Alert>
        )}
        <Formik
          initialValues={initialValues}
          validationSchema={requisitesSchema}
          onSubmit={handleSubmit}
          handleCancel={navigateBack}
          validateOnMount
          validateOnChange
          enableReinitialize
        >
          {(formik: FormikProps<Values>) => {
            const availableBanks = getAvailableBanks(formik.values);
            const showBanks =
              !!formik.values.paymentTypeId && !!formik.values.fiatCurrencyId;
            const showPaymentType = !!formik.values.fiatCurrencyId;

            return (
              <Form>
                <TouchErrorsOnLoad loadedValues={loadedValues} />
                {isTradeMethodNotSupported(formik.values) && (
                  <Alert severity="warning" className="tw-mb-6">
                    {tCommon(
                      'features.requisites.error.trade_method_unavailable',
                    )}
                  </Alert>
                )}
                <FormControls>
                  {canChangeStatus && (
                    <FormikRadioGroup
                      label={tFields('status')}
                      name="status"
                      options={statusOptions}
                      disabled={isBlocked}
                    />
                  )}

                  {isBlocked && queryResult.data && (
                    <RequisitesBlockedStatusLabel
                      status={queryResult.data.status}
                      statusDetails={queryResult.data.statusDetails}
                      blockedMessage={queryResult.data.blockedMessage}
                      blockedBy={queryResult.data.blockedBy}
                      blockedAt={queryResult.data.blockedAt}
                      blockedUntilAt={queryResult.data.blockedUntilAt}
                    />
                  )}

                  <FormikTextField name="name" label={tFields('name')} />

                  <Stack direction="row" spacing={1}>
                    <FormikSelect
                      className="tw-w-full"
                      label={tFields('group')}
                      name="groupId"
                      required
                      options={requisitesGroupsOptions}
                      noneOption
                    />
                    {isTrader && (
                      <FormControls>
                        <IconButton
                          color="primary"
                          size="medium"
                          onClick={() => setRequisitesGroupDialogOpen(true)}
                        >
                          <AddCircleOutlineRoundedIcon />
                        </IconButton>
                      </FormControls>
                    )}
                  </Stack>
                  <FormikSelect
                    label={tFields('currency')}
                    name="fiatCurrencyId"
                    options={fiatCurrenciesOptions}
                    disabled={!canChangeRequisitesPayment}
                    required
                    noneOption
                    onChange={(value) =>
                      handleOnChangeFiatCurrency(value, formik)
                    }
                  />

                  {showPaymentType && (
                    <PaymentTypeSelect
                      name="paymentTypeId"
                      paymentTypes={getAvailablePaymentTypes(formik.values)}
                      disabled={!canChangeRequisitesPayment}
                      required
                      noneOption
                      onChange={(value) => {
                        handleOnChangePaymentType(value, formik);
                      }}
                    />
                  )}

                  {showBanks && (
                    <Fragment>
                      <BankSelect
                        name="bankId"
                        disabled={!canChangeRequisitesPayment}
                        banks={availableBanks}
                        required
                        noneOption
                      />
                      <FormikYesNoRadioGroup
                        label={tFields('same_bank')}
                        name="sameBank"
                      />
                    </Fragment>
                  )}
                  <FormikYesNoRadioGroup
                    label={tFields('white_triangle')}
                    name="isWhiteTriangle"
                  />
                  <FormikYesNoRadioGroup
                    label={tFields('counterparties_only')}
                    tooltip={tFields('counterparties_only_description')}
                    name="counterpartiesOnly"
                  />
                  {isTurboDepositEnabled && (
                    <div>
                      <FormikYesNoRadioGroup
                        label={tFields('turbo_deposit')}
                        tooltip={tFields('turbo_deposit_description')}
                        name="allowTurboDeposit"
                      />
                    </div>
                  )}
                  {isNSPK(formik.values) && (
                    <FormikYesNoRadioGroup
                      label={tFields('qr_manager_enabled')}
                      tooltip={tFields('qr_manager_enabled_description')}
                      name="qrManagerEnabled"
                      onChange={(newValue) => {
                        setCurrentFields(
                          formik.values.paymentTypeId
                            ? normalizeCurrentFieldsToSet(
                                paymentTypeFields[formik.values.paymentTypeId]
                                  .fields || null,
                                {
                                  ...formik.values,
                                  qrManagerEnabled: newValue,
                                },
                              )
                            : null,
                        );
                        setTimeout(() => formik.validateForm());
                      }}
                    />
                  )}
                  {currentFields && (
                    <FormFields
                      fields={currentFields}
                      disabled={!canChangeRequisitesPayment}
                    />
                  )}
                  <Stack direction="row" spacing={3}>
                    <FormikNumericField
                      name="minOrderAmount"
                      label={tFields('min_order_amount')}
                      allowNegative={false}
                      defaultValue={0}
                      fullWidth
                    />
                    <FormikNumericField
                      name="maxOrderAmount"
                      label={tFields('max_order_amount')}
                      allowNegative={false}
                      defaultValue={0}
                      fullWidth
                    />
                  </Stack>

                  <Stack direction="row" spacing={3}>
                    <FormikNumericField
                      name="limitCountPerHour"
                      label={tFields('limit_count_per_hour')}
                      decimalScale={0}
                      allowNegative={false}
                      required
                      fullWidth
                    />
                    <FormikNumericField
                      name="limitCountPerDay"
                      label={tFields('limit_count_per_day')}
                      decimalScale={0}
                      allowNegative={false}
                      required
                      fullWidth
                    />
                    <FormikNumericField
                      label={tFields('limit_count_per_month')}
                      name="limitCountPerMonth"
                      decimalScale={0}
                      allowNegative={false}
                      required
                      fullWidth
                    />
                  </Stack>

                  <Stack direction="row" spacing={3}>
                    <FormikNumericField
                      name="limitSumPerHour"
                      label={tFields('limit_sum_per_hour')}
                      allowNegative={false}
                      required
                      fullWidth
                    />
                    <FormikNumericField
                      name="limitSumPerDay"
                      label={tFields('limit_sum_per_day')}
                      allowNegative={false}
                      required
                      fullWidth
                    />
                    <FormikNumericField
                      name="limitSumPerMonth"
                      label={tFields('limit_sum_per_month')}
                      allowNegative={false}
                      required
                      fullWidth
                    />
                  </Stack>
                  <Stack spacing={3}>
                    <FormikNumericField
                      name="limitActiveOrders"
                      label={tFields('limit_active_orders')}
                      allowNegative={false}
                      required
                      fullWidth
                    />
                    <FormikNumericField
                      name="completedOrdersInMinutes"
                      label={tFields('completed_orders_in_minutes')}
                      helperText={tFields(
                        'completed_orders_in_minutes_description',
                      )}
                      allowNegative={false}
                      required
                      fullWidth
                    />
                  </Stack>
                </FormControls>
                <FormActions>
                  <Button variant="outlined" onClick={navigateBack}>
                    {tCommon('buttons.cancel')}
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={formik.isSubmitting || !formik.isValid}
                    onClick={formik.submitForm}
                  >
                    {tCommon('buttons.save')}
                  </Button>
                </FormActions>
                <CreateRequisiteGroupDialog
                  open={createRequisiteGroupDialogOpen}
                  onClose={handleCreateRequisiteGroupDialogClose(formik)}
                />
              </Form>
            );
          }}
        </Formik>
      </div>
    </DataWrapper>
  );
};
